import { plural } from '../../shared/plural';

/**
 * Czynności wózkowe na liście i w szczegółach zlecenia.
 *
 * Czynność wózkowa to osobny rekord TMS przy zleceniu głównym — obsługa
 * wózkiem w miejscu załadunku albo rozładunku, a nie transport. Regułę
 * rozpoznania i powiązania liczy API (`user-offers/forklift-activity.ts`);
 * tu jest tylko odczyt tego, co przyszło, i podpisy dla planisty.
 */

/**
 * Wartości filtra `forklift` dotyczące wózków. Napisy są kontraktem z API —
 * pilnuje ich `forklift.test.ts`, czytając regułę backendu z dysku.
 *
 * Od 01.10.2026 to część filtra „Znaczniki” (`markers.ts`), stąd krótkie
 * etykiety — „wózki” obok „Z alertem przeplanowania”. Wartości bez zmian:
 * react-admin zapamiętał je użytkownikom.
 */
export const FORKLIFT_FILTER_CHOICES = [
  { id: 'hide', name: 'Ukryj wózki' },
  { id: 'only', name: 'Tylko wózki' },
  { id: 'all', name: 'Wszystkie' },
];

/** Domyślnie bank pokazuje transporty — czynności wózkowe są ukryte. */
export const FORKLIFT_FILTER_DEFAULT = 'hide';

export interface OrderRef {
  id: number;
  externalId: string | null;
}

export interface ForkliftInfo {
  isActivity: boolean;
  count: number | null;
  mainOrders: OrderRef[];
  activities: Array<OrderRef & { count: number | null }>;
}

const asRefs = (value: unknown): Array<OrderRef & { count: number | null }> =>
  Array.isArray(value)
    ? value
        .filter(
          (v): v is Record<string, unknown> =>
            !!v && typeof v === 'object' && typeof (v as { id?: unknown }).id === 'number',
        )
        .map((v) => ({
          id: v.id as number,
          externalId: typeof v.externalId === 'string' ? v.externalId : null,
          count: typeof v.count === 'number' ? v.count : null,
        }))
    : [];

/** Odczyt pola `forklift` z rekordu API; `null`, gdy wiersz nie dotyczy wózka. */
export function forkliftInfoOf(record: Record<string, unknown>): ForkliftInfo | null {
  const f = record.forklift;
  if (!f || typeof f !== 'object') return null;
  const o = f as Record<string, unknown>;
  return {
    isActivity: o.isActivity === true,
    count: typeof o.count === 'number' ? o.count : null,
    mainOrders: asRefs(o.mainOrders).map(({ id, externalId }) => ({ id, externalId })),
    activities: asRefs(o.activities),
  };
}

const refName = (r: OrderRef) => r.externalId ?? `#${r.id}`;

/**
 * Podpis do komórki listy: krótka etykieta i wyjaśnienie w podpowiedzi.
 *
 * Nieznanej liczby czynności nie zamieniamy na jedynkę — dopisek w TMS bywa
 * bez liczby, a „×1” wyglądałoby na daną z systemu klienta.
 *
 * Poprawa brzmienia 01.10.2026: zdania zamiast nawiasów z żargonem („poza
 * oknem importu”) i liczba odmieniona zamiast skrótu „czynn.”.
 */
export function forkliftSummary(
  info: ForkliftInfo | null,
): { label: string; detail: string; linked: boolean } | null {
  if (!info) return null;
  if (info.isActivity) {
    const linked = info.mainOrders.length > 0;
    return {
      label: info.count !== null ? `Czynność wózkowa ×${info.count}` : 'Czynność wózkowa',
      detail: linked
        ? `${info.mainOrders.length > 1 ? 'Dotyczy zleceń' : 'Dotyczy zlecenia'} ${info.mainOrders.map(refName).join(', ')}`
        : 'Zlecenia głównego nie ma w banku. Mogło nie zostać pobrane z TMS albo jego numer ma nietypową formę.',
      linked,
    };
  }
  if (info.activities.length === 0) return null;
  const counts = info.activities.map((a) => a.count);
  const total = counts.every((c): c is number => c !== null)
    ? counts.reduce((sum, c) => sum + c, 0)
    : null;
  return {
    label:
      total !== null
        ? `Wózek ×${total}`
        : `Wózek (${plural(info.activities.length, 'czynność', 'czynności', 'czynności')})`,
    detail: `Czynności wózkowe: ${info.activities.map(refName).join(', ')}`,
    linked: true,
  };
}
